import type { Point } from '@/lib/shapes/shape-coordinates'

import { formatPolygonPoints } from './chrome-points'

/**
 * Thickness of the invisible edge-resize strips, straddling the frame stroke. Half `HANDLE_HIT_PX`
 * on purpose: an edge runs the whole side, so a hit zone as deep as a node's would eat a wide band
 * of the interior that is supposed to move the selection.
 */
const EDGE_HIT_PX = 8

/**
 * The edge-resize strip along the side from `a` to `b`, as an SVG `points` string: a quad
 * `EDGE_HIT_PX` deep, centered on the side. `null` when a corner is missing or the side is
 * shorter than 1px. Screen px in and out.
 */
export function resolveEdgeHitQuad(a: Point | undefined, b: Point | undefined): string | null {
  if (!a || !b) return null

  const dx = b.x - a.x
  const dy = b.y - a.y
  const length = Math.hypot(dx, dy)

  if (length < 1) return null

  // Outward normal, scaled to half the strip. Straddling the stroke means the grab zone
  // works whether the pointer is just inside or just outside the frame.
  const nx = (-dy / length) * (EDGE_HIT_PX / 2)
  const ny = (dx / length) * (EDGE_HIT_PX / 2)

  return formatPolygonPoints([
    { x: a.x + nx, y: a.y + ny },
    { x: b.x + nx, y: b.y + ny },
    { x: b.x - nx, y: b.y - ny },
    { x: a.x - nx, y: a.y - ny },
  ])
}
